import React, { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import axios, { AxiosResponse } from 'axios';

import { queryStrings } from '@trqk/resolvers';
import { Button } from '@trqk/components';
import { getWebpackEnvVar, locale } from '@trqk/utils';

import { useTokenModal, useToast } from '../../../../../../../hooks';
import { MAIN_CONTENT_ID } from '../../../../../../../constants';

const { GET_SESSION_CURRENT_USER } = queryStrings;

interface HostedPageButtonProps {
    addOns?: ChargebeeAddOnLineItem[];
    chargebeeInstance: null | ChargebeeInstance;
    label: string;
    /**
     * Callback invoked when the button is visible to the user. This is implemented with
     * IntersectionObserver.
     */
    onButtonViewed(): void;
    openCheckoutOptions: ChargebeeOpenCheckoutOptionsPartial;
    redirectUrl: string;
    planId: ChargebeePlanId | null;
}

/**
 * A button that uses getChargebeeHostedPage to open the Chargebee modal.
 * @param props - {@link HostedPageButtonProps}
 * @returns JSX
 */
export const HostedPageButton = ({
    addOns = [],
    chargebeeInstance,
    label,
    onButtonViewed,
    openCheckoutOptions,
    redirectUrl,
    planId,
}: HostedPageButtonProps): JSX.Element => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { getToken } = useTokenModal();
    const { add: addToast } = useToast();

    const addOnsString = JSON.stringify(addOns);

    const getChargebeeHostedPage = useCallback(
        (): Promise<ChargebeeHostedPage> =>
            new Promise(resolve => {
                getToken((token: string) => {
                    axios({
                        method: 'post',
                        url: trqkDirectoryUrl,
                        headers: {
                            Authorization: token,
                        },
                        data: {
                            query: `
                            query getChargebeeHostedPage($input: chargebeeHostedPageInput) {
                                getChargebeeHostedPage(input: $input) {
                                    id
                                    type
                                    object
                                    resource_version
                                    url
                                    state
                                    created_at
                                    expires_at
                                    updated_at
                                    embed
                                }
                            }
                        `,
                            variables: {
                                input: {
                                    add_ons: JSON.parse(addOnsString),
                                    redirect_url: redirectUrl,
                                    plan_id: planId,
                                },
                            },
                        },
                    })
                        .then(
                            (
                                response: AxiosResponse<{
                                    data: {
                                        getChargebeeHostedPage: ChargebeeHostedPage;
                                    };
                                }>,
                            ): void => {
                                const chargebeeHostedPage =
                                    response?.data?.data
                                        ?.getChargebeeHostedPage ?? null;

                                if (chargebeeHostedPage === null) {
                                    addToast({
                                        kind: 'warning-banner',
                                        content:
                                            locale.enUS.ACCOUNT.SECTIONS
                                                .PAYMENT_PLAN.TOASTS
                                                .PROBLEM_OPENING_CHECKOUT,
                                    });
                                }

                                delete chargebeeHostedPage.__typename;

                                resolve(chargebeeHostedPage);
                            },
                        )
                        .catch(() => {
                            addToast({
                                kind: 'warning-banner',
                                content:
                                    locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN
                                        .TOASTS.PROBLEM_OPENING_CHECKOUT,
                            });
                        });
                });
            }),
        [addOnsString, trqkDirectoryUrl, userId, planId],
    );

    const targetRef = useRef<HTMLDivElement | null>(null);

    const observerCallback: IntersectionObserverCallback = useCallback(
        (entries): void => {
            entries.forEach(
                ({ isIntersecting }: IntersectionObserverEntry): void => {
                    if (isIntersecting) {
                        onButtonViewed();
                    }
                },
            );
        },
        [],
    );

    const observerRef = useRef<IntersectionObserver | null>(
        new IntersectionObserver(observerCallback, {
            root: document.getElementById(MAIN_CONTENT_ID),
            rootMargin: '0px',
            threshold: 1,
        }),
    );

    useEffect(() => {
        if (observerRef.current && targetRef.current) {
            observerRef.current.observe(targetRef.current);
        }

        return (): void => {
            if (observerRef.current !== null) {
                observerRef.current.disconnect();
            }
        };
    }, [observerRef.current, targetRef.current]);

    return (
        <Button
            autoWidth={true}
            color="primary-light-bg"
            handleClick={() => {
                if (chargebeeInstance && userId) {
                    chargebeeInstance.openCheckout({
                        hostedPage: getChargebeeHostedPage,
                        ...openCheckoutOptions,
                    });
                }
            }}
        >
            <div
                ref={targetRef}
                className="text-sm font-medium tracking-wide text-center px-10"
            >
                {label}
            </div>
        </Button>
    );
};
